import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Upload, Calendar as CalendarIcon, CheckCircle2, XCircle, Clock, Eye, Image } from 'lucide-react';
import { toast } from 'sonner';
import { api, apiMultipart } from '../utils/api';

const WeeklyCalendar = ({ projectId, userRole, projectStartDate }) => {
  const [weeks, setWeeks] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [uploadData, setUploadData] = useState({ notes: '', files: [] });
  const [uploading, setUploading] = useState(false);
  const [approvalFeedback, setApprovalFeedback] = useState('');

  useEffect(() => {
    generateWeeks();
    if (projectId) {
      fetchWeeklyUpdates();
    }
  }, [projectId]);

  const generateWeeks = () => {
    const startDate = new Date(projectStartDate || '2024-06-01');
    const today = new Date();
    const weeksList = [];
    let currentWeekStart = new Date(startDate);

    while (currentWeekStart <= today || weeksList.length < 12) {
      const weekEnd = new Date(currentWeekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const weekId = `week_${currentWeekStart.getFullYear()}_${currentWeekStart.getMonth() + 1}_${currentWeekStart.getDate()}`;
      
      weeksList.push({
        week_id: weekId,
        week_start: currentWeekStart.toISOString().split('T')[0],
        week_end: weekEnd.toISOString().split('T')[0],
        uploaded: false,
        photos: [],
        notes: '',
        status: 'pending',
        timeStatus: getWeekStatus(currentWeekStart, weekEnd, today)
      });

      currentWeekStart = new Date(weekEnd);
      currentWeekStart.setDate(currentWeekStart.getDate() + 1);
    }

    setWeeks(weeksList);
  };

  const getWeekStatus = (weekStart, weekEnd, today) => {
    if (weekEnd < today) return 'past';
    if (weekStart <= today && weekEnd >= today) return 'current';
    return 'future';
  };

  const fetchWeeklyUpdates = async () => {
    try {
      const response = await api.get(`/weekly-updates?project_id=${projectId}`);
      const updates = response.data;

      setWeeks(prevWeeks => prevWeeks.map(week => {
        const update = updates.find(u => u.week_id === week.week_id);
        if (update) {
          return { 
            ...week, 
            uploaded: update.uploaded, 
            photos: update.photos || [],
            notes: update.notes || '',
            status: update.status || 'pending',
            admin_feedback: update.admin_feedback
          };
        }
        return week;
      }));
    } catch (error) {
      console.error('Failed to fetch weekly updates:', error);
    }
  };

  const getWeekColor = (week) => {
    // Approval status colors
    if (week.status === 'approved') return 'bg-green-100 border-green-400 text-green-900';
    if (week.status === 'pending_approval') return 'bg-blue-100 border-blue-400 text-blue-900';
    if (week.status === 'rejected') return 'bg-red-100 border-red-400 text-red-900';
    
    // Time-based colors for non-uploaded weeks
    if (week.timeStatus === 'past') return 'bg-orange-50 border-orange-300 text-orange-800';
    if (week.timeStatus === 'current') return 'bg-yellow-100 border-yellow-400 text-yellow-900';
    return 'bg-stone-50 border-stone-200 text-stone-600';
  };

  const getStatusBadge = (week) => {
    if (week.status === 'approved') {
      return <Badge className="bg-green-600 text-white text-xs">Approved</Badge>;
    }
    if (week.status === 'pending_approval') {
      return <Badge className="bg-blue-600 text-white text-xs">Pending Approval</Badge>;
    }
    if (week.status === 'rejected') {
      return <Badge className="bg-red-600 text-white text-xs">Rejected</Badge>;
    }
    if (week.uploaded) {
      return <Badge className="bg-stone-600 text-white text-xs">Uploaded</Badge>;
    }
    return null;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (uploadData.files.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('week_id', selectedWeek.week_id);
      formData.append('project_id', projectId);
      formData.append('notes', uploadData.notes);

      uploadData.files.forEach(file => {
        formData.append('files', file);
      });

      await apiMultipart.post('/weekly-updates/upload', formData);

      toast.success('Weekly update uploaded and sent for admin approval');
      setShowUploadModal(false);
      setUploadData({ notes: '', files: [] });
      fetchWeeklyUpdates();
    } catch (error) {
      toast.error('Failed to upload weekly update');
    } finally {
      setUploading(false);
    }
  };

  const handleApproval = async (action) => {
    try {
      await api.post(`/weekly-updates/${selectedWeek.week_id}/approve`, {
        action,
        feedback: approvalFeedback
      });

      toast.success(`Weekly update ${action}d successfully`);
      setShowApprovalModal(false);
      setApprovalFeedback('');
      fetchWeeklyUpdates();
    } catch (error) {
      toast.error(`Failed to ${action} weekly update`);
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return `${startDate.getDate()} ${startDate.toLocaleDateString('en-IN', { month: 'short' })} - ${endDate.getDate()} ${endDate.toLocaleDateString('en-IN', { month: 'short' })}`;
  };

  const handleWeekClick = (week) => {
    setSelectedWeek(week);
    
    if (userRole === 'engineer' && !week.uploaded) {
      setShowUploadModal(true);
    } else if (userRole === 'admin' && week.status === 'pending_approval') {
      setShowApprovalModal(true);
    } else if (week.uploaded && (week.status === 'approved' || userRole !== 'customer')) {
      setShowViewModal(true);
    }
  };

  return (
    <div>
      <Card className="border-stone-200 rounded-lg shadow-sm">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold flex items-center">
            <CalendarIcon className="mr-2 h-6 w-6 text-blue-700" />
            Weekly Progress Calendar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Legend */}
          <div className="mb-6 flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-green-100 border-2 border-green-400 rounded"></div>
              <span>Approved</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-blue-100 border-2 border-blue-400 rounded"></div>
              <span>Pending Approval</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-red-100 border-2 border-red-400 rounded"></div>
              <span>Rejected</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-orange-50 border-2 border-orange-300 rounded"></div>
              <span>Missed</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 bg-yellow-100 border-2 border-yellow-400 rounded"></div>
              <span>Current Week</span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {weeks.map((week) => (
              <div
                key={week.week_id}
                onClick={() => handleWeekClick(week)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${getWeekColor(week)}`}
                data-testid={`week-${week.week_id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm">
                    {formatDateRange(week.week_start, week.week_end)}
                  </span>
                  {week.photos.length > 0 && (
                    <Image className="h-4 w-4 opacity-60" />
                  )}
                </div>
                
                {getStatusBadge(week)}
                
                {week.notes && (
                  <p className="text-xs mt-2 line-clamp-2 opacity-80">{week.notes}</p>
                )}
                
                {/* Engineer: Upload prompt for non-uploaded weeks */}
                {userRole === 'engineer' && !week.uploaded && week.timeStatus !== 'future' && (
                  <div className="mt-2 flex items-center text-xs opacity-70">
                    <Upload className="h-3 w-3 mr-1" />
                    Click to upload
                  </div>
                )}
                
                {/* Admin: Review prompt for pending approval */}
                {userRole === 'admin' && week.status === 'pending_approval' && (
                  <div className="mt-2 flex items-center text-xs text-blue-700">
                    <Clock className="h-3 w-3 mr-1" />
                    Click to review
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Upload Modal (Engineer) */}
      <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Weekly Progress</DialogTitle>
            <DialogDescription>
              {selectedWeek && formatDateRange(selectedWeek.week_start, selectedWeek.week_end)}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpload} className="space-y-4">
            <div className="space-y-2">
              <Label>Site Photos *</Label>
              <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center">
                <Input
                  id="photos"
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => setUploadData({ ...uploadData, files: Array.from(e.target.files) })}
                />
                <label htmlFor="photos" className="cursor-pointer">
                  {uploadData.files.length > 0 ? (
                    <p className="text-green-700 font-medium">{uploadData.files.length} file(s) selected</p>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-stone-400 mx-auto mb-2" />
                      <p className="text-stone-600">Click to upload photos</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes & Remarks</Label>
              <Textarea
                id="notes"
                value={uploadData.notes}
                onChange={(e) => setUploadData({ ...uploadData, notes: e.target.value })}
                placeholder="Describe the work completed this week..."
                rows={4}
              />
            </div>

            <div className="flex space-x-3">
              <Button type="button" variant="outline" onClick={() => setShowUploadModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" disabled={uploading} className="flex-1 bg-blue-900 hover:bg-blue-800">
                {uploading ? 'Uploading...' : 'Upload for Review'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Modal (View uploaded content) */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              Weekly Update
              {selectedWeek && getStatusBadge(selectedWeek)}
            </DialogTitle>
            <DialogDescription>
              {selectedWeek && formatDateRange(selectedWeek.week_start, selectedWeek.week_end)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedWeek?.notes && (
              <div className="p-4 bg-stone-50 rounded-lg">
                <Label className="text-sm text-stone-600">Engineer Notes</Label>
                <p className="mt-1">{selectedWeek.notes}</p>
              </div>
            )}

            {selectedWeek?.admin_feedback && (
              <div className="p-4 bg-blue-50 rounded-lg">
                <Label className="text-sm text-blue-600">Admin Feedback</Label>
                <p className="mt-1">{selectedWeek.admin_feedback}</p>
              </div>
            )}

            {selectedWeek?.photos?.length > 0 && (
              <div className="space-y-2">
                <Label>Site Photos ({selectedWeek.photos.length})</Label>
                <div className="grid grid-cols-2 gap-4">
                  {selectedWeek.photos.map((photo, idx) => (
                    <div key={idx} className="aspect-video bg-stone-200 rounded-lg overflow-hidden">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.split('/').pop()}`}
                        alt={`Site photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Approval Modal (Admin) */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              Review Weekly Update
              <Badge className="ml-2 bg-blue-600 text-white">Pending Approval</Badge>
            </DialogTitle>
            <DialogDescription>
              {selectedWeek && formatDateRange(selectedWeek.week_start, selectedWeek.week_end)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedWeek?.notes && (
              <div className="p-4 bg-stone-50 rounded-lg">
                <Label className="text-sm text-stone-600">Engineer Notes</Label>
                <p className="mt-1">{selectedWeek.notes}</p>
              </div>
            )}

            {selectedWeek?.photos?.length > 0 && (
              <div className="space-y-2">
                <Label>Site Photos ({selectedWeek.photos.length})</Label>
                <div className="grid grid-cols-2 gap-4 max-h-64 overflow-y-auto">
                  {selectedWeek.photos.map((photo, idx) => (
                    <div key={idx} className="aspect-video bg-stone-200 rounded-lg overflow-hidden">
                      <img 
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.split('/').pop()}`}
                        alt={`Site photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="feedback">Feedback (Optional)</Label>
              <Textarea
                id="feedback"
                value={approvalFeedback}
                onChange={(e) => setApprovalFeedback(e.target.value)}
                placeholder="Add feedback for the engineer..."
                rows={3}
              />
            </div>

            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                onClick={() => handleApproval('reject')}
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <Button 
                onClick={() => handleApproval('approve')}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WeeklyCalendar;
